// ProximityStats component - Shows proximity analysis statistics

import React from 'react';
import { TrendingUp, MapPin, Building2, Clock } from 'lucide-react';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import { formatDistance, formatNumber } from '../../utils/formatters';
import { calculateCoverageStats } from '../../services/distance';

const ProximityStats = ({ pod, outlets = [], className = '' }) => {
  if (!pod) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-accent-500" />
            Proximity Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-dark-400">
            <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Select a POD to see analysis</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate stats
  const stats = calculateCoverageStats(pod, outlets, [500, 1000, 2000]);

  // Calculate coverage score (0-100)
  const coverageScore = outlets.length > 0
    ? Math.min(100, Math.round(
        ((stats.coverage[500] || 0) * 10) +
        ((stats.coverage[1000] || 0) * 5) +
        ((stats.coverage[2000] || 0) * 2)
      ))
    : 0;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-accent-500" />
          Proximity Analysis
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Coverage Score */}
        <div className="text-center p-4 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl">
          <p className="text-sm text-dark-600 mb-1">Coverage Score</p>
          <p className="text-4xl font-bold text-primary-600">{coverageScore}</p>
          <p className="text-xs text-dark-500 mt-1">out of 100</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-dark-50 rounded-lg text-center">
            <Building2 className="w-5 h-5 mx-auto mb-1 text-secondary-500" />
            <p className="text-2xl font-bold text-dark-900">{outlets.length}</p>
            <p className="text-xs text-dark-500">Total Outlets</p>
          </div>
          <div className="p-3 bg-dark-50 rounded-lg text-center">
            <MapPin className="w-5 h-5 mx-auto mb-1 text-accent-500" />
            <p className="text-2xl font-bold text-dark-900">
              {stats.nearestOutlet ? formatDistance(stats.nearestOutlet) : '-'}
            </p>
            <p className="text-xs text-dark-500">Nearest</p>
          </div>
        </div>

        {/* Coverage Breakdown */}
        <div>
          <p className="text-sm font-medium text-dark-700 mb-2">Coverage Breakdown</p>
          <div className="space-y-2">
            {[
              { radius: 500, label: '500m', color: 'bg-accent-500', desc: 'Walking distance' },
              { radius: 1000, label: '1km', color: 'bg-secondary-500', desc: 'Short drive' },
              { radius: 2000, label: '2km', color: 'bg-primary-500', desc: 'Medium drive' },
            ].map(({ radius, label, color, desc }) => {
              const count = stats.coverage[radius] || 0;
              const percentage = outlets.length > 0 ? (count / outlets.length) * 100 : 0;

              return (
                <div key={radius} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-dark-600">{label}</span>
                    <span className="font-medium text-dark-900">{count} outlets</span>
                  </div>
                  <div className="w-full bg-dark-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${color} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Average Distance */}
        {stats.averageDistance > 0 && (
          <div className="flex items-center justify-between p-3 bg-dark-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-dark-500" />
              <span className="text-sm text-dark-600">Average Distance</span>
            </div>
            <span className="font-semibold text-dark-900">
              {formatDistance(stats.averageDistance)}
            </span>
          </div>
        )}

        {/* Sales Pitch Note */}
        <div className="p-3 border-l-4 border-secondary-500 bg-secondary-50 rounded-r-lg">
          <p className="text-sm text-secondary-800">
            <strong>Sales Note:</strong> This POD has excellent proximity to {stats.coverage[1000] || 0} fast food outlets
            within 1km, making it ideal for food & beverage advertising campaigns.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProximityStats;
